//============================================================================
// Name        : TableColumn.tsx
// Purpose     : Implement Edit Schedule Calencar view Day block
// Created     : 2019-08-19
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import { css, jsx } from "@emotion/core";
import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { IUpdateStateAction } from "./actions/editScheduleActions";
import { EditScheduleActions, IProduction, IReduxReducers, IStorageUnit, IZone } from "./types";

const leftBlueCell = css`
    background-color: #2770a8;
    color: #ffffff;
    padding-top: 5px;
    padding-bottom: 5px;
    text-align: left;
    font-size: 12px;
    justify-content: center;
`;
const leftLightBlueCell = css`
    background-color: #8ab4d5;
`;
const columnScroll = css`
    overflow-y: hidden;
    // overflow-y: scroll;
    // &::-webkit-scrollbar {
    //     width: 0 !important;
    // }
`;
const headerStyle = css`
    text-align: center;
    height: 45px;
`;
const prodBlock = css`
    margin-top: 50px;
    padding: 5px 15px;
    text-align: center;
    border: 1px #ccc solid;

    & > div {
        padding: 4px;
    }
`;
const prodBlock2 = css`
    margin-top: 19px;
`;
const qtyBlock = css`
    margin-top: 24px;
    padding: 5px 15px;
    text-align: center;
    border: 1px #ccc solid;
    font-size: 12px;

    & > div {
        padding: 0;
    }
`;
const lightBlueCell = css`
    background-color: #6194bd;
    color: white;
`;
const lightestBlueCell = css`
    background-color: #8ab4d5;
    color: white;
`;

export interface ITableColumnState {
    children: any;
    height?: number;
    width?: number;
    headerHeight: number;
    headerTitle: string;
    headerSubtitle?: string;
    footer: string;
    date?: string;
    pastDue: boolean;
    showAsLbs?: boolean;
    showAsCases?: boolean;
    zones?: IZone[];
    productions?: IProduction[];
    pastDueProductions?: IProduction[];
    storageUnit: IStorageUnit;
}

class TableColumn extends React.Component<ITableColumnState> {
    //========================================================================
    // Calculate Scheduled values
    //========================================================================

    getQtyScheduled = (zone: IZone) => {
        return this.props.productions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.needed_qty, 0);
    }

    getQtyCompleted = (zone: IZone) => {
        return this.props.productions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.qty_produced, 0);
    }

    getQtyRemaining = (zone: IZone) => {
        return this.props.productions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.pending_qty, 0);
    }

    getTotalQtyCompleted = () => {
        return this.props.productions.filter(p => p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.qty_produced, 0);
    }

    getTotalQtyRemaining = () => {
        return this.props.productions.filter(p => p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.pending_qty, 0);
    }

    getPercentToCompletion = () => {
        let scheduled: number;
        const totalsCol = this.props.headerTitle.toLowerCase().includes("total");
        let completed: number;

        // Different logic depending on a normal column or the totals column
        if (totalsCol) {
            scheduled = this.props.productions.reduce((acc, p) => acc + p.needed_qty, 0);
            completed = this.props.productions.reduce((accTotal, p) => accTotal + p.qty_produced, 0);
        }
        else {
            const dayProductions = this.props.productions.filter(p => p.scheduled_date == this.props.date);
            if (dayProductions.length > 0) {
                scheduled = dayProductions.reduce((acc, p) => acc + p.needed_qty, 0);
            }
            else {
                scheduled = 0;
            }
            completed = this.getTotalQtyCompleted();
        }

        const pending = (scheduled - completed) / scheduled;
        const percent = Math.floor( (pending * 100) );
        return percent ? percent.toFixed(2) : 0.00;
    }

    //========================================================================
    // Calculate Past-Due values
    //========================================================================

    getPastDueQtyScheduled = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.needed_qty, 0);
    }

    getPastDueQtyScheduledTotal = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id)
            .reduce((acc, p) => acc + p.needed_qty, 0);
    }

    getPastDueQtyCompleted = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.qty_produced, 0);
    }

    getPastDueQtyCompletedTotal = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id)
            .reduce((acc, p) => acc + p.qty_produced, 0);
    }

    getPastDueQtyRemaining = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id && p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.pending_qty, 0);
    }

    getPastDueQtyRemainingTotal = (zone: IZone) => {
        return this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id)
            .reduce((acc, p) => acc + p.pending_qty, 0);
    }

    getPastDueTotalQtyCompleted = () => {
        return this.props.pastDueProductions.filter(p => p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.qty_produced, 0);
    }

    getPastDueTotalQtyRemaining = () => {
        return this.props.pastDueProductions.filter(p => p.scheduled_date == this.props.date)
            .reduce((acc, p) => acc + p.pending_qty, 0);
    }

    getPastDuePercentToCompletion = () => {
        let scheduled: number;
        const totalsCol = this.props.headerTitle.toLowerCase().includes("total");

        // Different logic depending on a normal column or the totals column
        if (totalsCol) {
            scheduled = this.props.pastDueProductions.reduce((acc, p) => acc + p.needed_qty, 0);
        }
        else {
            scheduled = this.props.pastDueProductions.filter(p => p.scheduled_date == this.props.date)
                .reduce((acc, p) => acc + p.needed_qty, 0);
        }

        const completed = this.getPastDueTotalQtyCompleted();
        const pending = (scheduled - completed) / scheduled;
        return Math.floor(pending * 100).toFixed(2);
    }

    //========================================================================
    // Global quantities per zone
    //========================================================================

    getZoneScheduledTotal = (zone: IZone): number => {
        const presentProductions = this.props.productions.filter(p => p.sub_zone_id == zone.id);
        const pastDueProductions = this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id);

        // Error check in case there's no production past or present scheduled for this zone
        if (presentProductions.length == 0 && pastDueProductions.length == 0) {
            return 0;
        }

        let total = 0;
        total += presentProductions.reduce((accTotal, p) => accTotal + p.needed_qty, 0);
        total += pastDueProductions.reduce((accTotal, p) => accTotal + p.needed_qty, 0);
        return total;
    }
    getZoneCompletedTotal = (zone: IZone): number => {
        const presentProductions = this.props.productions.filter(p => p.sub_zone_id == zone.id);
        const pastDueProductions = this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id);

        // Error check in case there's no production past or present scheduled for this zone
        if (presentProductions.length == 0 && pastDueProductions.length == 0) {
            return 0;
        }

        let total = 0;
        total += presentProductions.reduce((accTotal, p) => accTotal + p.qty_produced, 0);
        total += pastDueProductions.reduce((accTotal, p) => accTotal + p.qty_produced, 0);
        return total;
    }
    getZoneRemainingTotal = (zone: IZone): number => {
        const presentProductions = this.props.productions.filter(p => p.sub_zone_id == zone.id);
        const pastDueProductions = this.props.pastDueProductions.filter(p => p.sub_zone_id == zone.id);

        // Error check in case there's no production past or present scheduled for this zone
        if (presentProductions.length == 0 && pastDueProductions.length == 0) {
            return 0;
        }

        let total = 0;
        total += presentProductions.reduce((accTotal, p) => accTotal + p.pending_qty, 0);
        total += pastDueProductions.reduce((accTotal, p) => accTotal + p.pending_qty, 0);
        return total;
    }

    getGlobalQtyCompleted = () => {
        const presentTotal = this.props.productions.reduce((acc, p) => acc + p.qty_produced, 0);
        const pastDueTotal = this.props.pastDueProductions.reduce((acc, p) => acc + p.qty_produced, 0);
        return presentTotal + pastDueTotal;
    }

    getGlobalQtyRemaining = () => {
        const presentTotal = this.props.productions.reduce((acc, p) => acc + p.pending_qty, 0);
        const pastDueTotal = this.props.pastDueProductions.reduce((acc, p) => acc + p.pending_qty, 0);
        return presentTotal + pastDueTotal;
    }

    getPercentGlobalToCompletion = () => {
        const presentTotal = this.props.productions.reduce((acc, p) => acc + p.needed_qty, 0);
        const pastDueTotal = this.props.pastDueProductions.reduce((acc, p) => acc + p.needed_qty, 0);
        const scheduled = presentTotal + pastDueTotal;

        const completed = this.getGlobalQtyCompleted();
        const pending = (scheduled - completed) / scheduled;
        const percent = Math.floor(pending * 100);
        return percent ? percent.toFixed(2) : 0.00;
    }

    //========================================================================
    // Helpers
    //========================================================================

    valueInUnits = (value: number): JSX.Element => {
        const cases = value / parseFloat(this.props.storageUnit.measure_unit);
        return (
            <React.Fragment>
                {this.props.showAsLbs && <div>{value}</div>}
                {this.props.showAsCases && <div>{cases}</div>}
            </React.Fragment>
        );
    }

    getZoneStyle = (key: number) => {
        return key >= 1 ? [prodBlock, prodBlock2] : [prodBlock];
    }

    render() {
        const middleAlign = this.props.headerSubtitle ? {} : { display: "flex", alignItems: "center" };
        const totalsCol = this.props.headerTitle.toLowerCase().includes("total");

        let globalTotalsCol = false;
        if (this.props.headerSubtitle) {
            globalTotalsCol = this.props.headerSubtitle.toLowerCase().includes("+ past due");
        }

        return (
            <React.Fragment>
                <div css={{ width: this.props.width ? this.props.width : "auto", textAlign: "center", minWidth: 95 }}>
                    <div css={[leftBlueCell, headerStyle, middleAlign, { paddingLeft: 15, paddingRight: 15 }]}>
                        {!this.props.headerSubtitle && this.props.headerTitle}
                        {this.props.headerSubtitle && <div>{this.props.headerTitle}</div>}
                        {this.props.headerSubtitle && <div>{this.props.headerSubtitle}</div>}
                    </div>

                    <div css={[columnScroll, { height: this.props.height }]}>
                        {this.props.children}
                    </div>

                    <div css={[leftBlueCell, leftLightBlueCell, middleAlign, { display: "flex" }]}>{this.props.footer}</div>

                    {/* Each Building Productions  */}
                    {this.props.pastDue && !totalsCol && this.props.zones.map((zone, key) =>
                        <React.Fragment key={key}>
                            <div css={this.getZoneStyle(key)}>
                                <div>{this.valueInUnits(this.getPastDueQtyScheduled(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyCompleted(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyRemaining(zone))}</div>
                            </div>
                            {totalsCol && <div css={{ height: 29 }}></div>}
                            {!totalsCol && <button disabled className="man-btn man-sm-btn man-green-btn" css={{ paddingTop: 2, marginTop: 3, minWidth: 60 }}>Messages</button>}
                        </React.Fragment>
                    )}
                    {!this.props.pastDue && !totalsCol && this.props.zones.map((zone, key) =>
                        <React.Fragment key={key}>
                            <div css={this.getZoneStyle(key)}>
                                <div>{this.valueInUnits(this.getQtyScheduled(zone))}</div>
                                <div>{this.valueInUnits(this.getQtyCompleted(zone))}</div>
                                <div>{this.valueInUnits(this.getQtyRemaining(zone))}</div>
                            </div>
                            {totalsCol && <div css={{ height: 29 }}></div>}
                            {!totalsCol && <button disabled className="man-btn man-sm-btn man-green-btn" css={{ paddingTop: 2, marginTop: 3, minWidth: 60 }}>Messages</button>}
                        </React.Fragment>
                    )}

                    {/* Building Totals */}
                    {this.props.pastDue && totalsCol && this.props.zones.map((zone, key) =>
                        <React.Fragment key={key}>
                            <div css={this.getZoneStyle(key)}>
                                <div>{this.valueInUnits(this.getPastDueQtyScheduledTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyCompletedTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyRemainingTotal(zone))}</div>
                            </div>
                            {totalsCol && <div css={{ height: 29 }}></div>}
                            {!totalsCol && <button disabled className="man-btn man-sm-btn man-green-btn" css={{ paddingTop: 2, marginTop: 3, minWidth: 60 }}>Messages</button>}
                        </React.Fragment>
                    )}
                    {!this.props.pastDue && totalsCol && !globalTotalsCol && this.props.zones.map((zone, key) =>
                        <React.Fragment key={key}>
                            <div css={this.getZoneStyle(key)}>
                                <div>{this.valueInUnits(this.getPastDueQtyScheduledTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyCompletedTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getPastDueQtyRemainingTotal(zone))}</div>
                            </div>
                            {totalsCol && <div css={{ height: 29 }}></div>}
                            {!totalsCol && <button disabled className="man-btn man-sm-btn man-green-btn" css={{ paddingTop: 2, marginTop: 3, minWidth: 60 }}>Messages</button>}
                        </React.Fragment>
                    )}
                    {!this.props.pastDue && totalsCol && globalTotalsCol && this.props.zones.map((zone, key) =>
                        <React.Fragment key={key}>
                            <div css={this.getZoneStyle(key)}>
                                <div>{this.valueInUnits(this.getZoneScheduledTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getZoneCompletedTotal(zone))}</div>
                                <div>{this.valueInUnits(this.getZoneRemainingTotal(zone))}</div>
                            </div>
                            {totalsCol && <div css={{ height: 29 }}></div>}
                            {!totalsCol && <button disabled className="man-btn man-sm-btn man-green-btn" css={{ paddingTop: 2, marginTop: 3, minWidth: 60 }}>Messages</button>}
                        </React.Fragment>
                    )}

                    {/* Total Qty Completed */}
                    {this.props.pastDue && <div css={[qtyBlock, lightestBlueCell]}>{this.valueInUnits(this.getPastDueTotalQtyCompleted())}</div>}
                    {!this.props.pastDue && !globalTotalsCol && <div css={[qtyBlock, lightestBlueCell]}>{this.valueInUnits(this.getTotalQtyCompleted())}</div>}
                    {!this.props.pastDue && globalTotalsCol && <div css={[qtyBlock, lightestBlueCell]}>{this.valueInUnits(this.getGlobalQtyCompleted())}</div>}

                    {/* Pending Qty */}
                    {this.props.pastDue && <div css={[qtyBlock, lightBlueCell]}>{this.valueInUnits(this.getPastDueTotalQtyRemaining())}</div>}
                    {!this.props.pastDue && !globalTotalsCol && <div css={[qtyBlock, lightBlueCell]}>{this.valueInUnits(this.getTotalQtyRemaining())}</div>}
                    {!this.props.pastDue && globalTotalsCol && <div css={[qtyBlock, lightBlueCell]}>{this.valueInUnits(this.getGlobalQtyRemaining())}</div>}

                    {/* Percent Remaining */}
                    {this.props.pastDue && <div css={[qtyBlock, lightBlueCell]}>{this.getPastDuePercentToCompletion()}%</div>}
                    {!this.props.pastDue && !globalTotalsCol && <div css={[qtyBlock, lightBlueCell]}>{this.getPercentToCompletion()}%</div>}
                    {!this.props.pastDue && globalTotalsCol && <div css={[qtyBlock, lightBlueCell]}>{this.getPercentGlobalToCompletion()}%</div>}
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: IReduxReducers) => ({
    showAsLbs:          state.editSchedule.showAsLbs,
    showAsCases:        state.editSchedule.showAsCases,
    zones:              state.editSchedule.zones,
    productions:        state.editSchedule.productions,
    pastDueProductions: state.editSchedule.pastDueProductions,
    storageUnit:        state.editSchedule.storageUnit
});

const mapDispatchToProps = dispatch => ({
    updateState: (newState: object) => dispatch({
        type: EditScheduleActions.UpdateState,
        payload: newState
    } as IUpdateStateAction)
});

export default connect(mapStateToProps, mapDispatchToProps)(TableColumn);
